import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Grid, MenuProps, Badge } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// services
import { RootState } from 'stores/store';

//components
import Icon from 'utils/icon';
import authService from 'core/services/auth';
import { isRTL } from './../../../services/language';
import { onGetTransferApprovalsCount } from 'modules/main/companyInformation/transactionsLog/services/transactionsService';

const { useBreakpoint } = Grid;

interface MainDashboardMenuProps {
	collapsed: boolean;
	changeCollapsed: (value: boolean) => void;
}

function MainDashboardMenu({ collapsed, changeCollapsed }: MainDashboardMenuProps) {
	const screens = useBreakpoint();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();

	//global states
	const company = useSelector((state: RootState) => state.company);

	// local states
	const [current, setCurrent] = useState('');
	const [planOpen, setPlanOpen] = useState(false);
	const [pendingCount, setPendingCount] = useState(0);

	const [menuItems1, setMenuItems1] = useState<any[]>([]);
	const [menuItems2, setMenuItems2] = useState<any[]>([]);
	const [menuItems3, setMenuItems3] = useState<any[]>([]);

	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		if (company?.profile_status !== 'Active' || !company?.usage_start_at) return;

		const fetchCount = async () => {
			try {
				const data = await onGetTransferApprovalsCount();
				setPendingCount(data?.pending_count ?? 0);

			} catch {
				// silently ignore errors
			}
		};

		fetchCount();
		intervalRef.current = setInterval(fetchCount, 10000);

		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, [company?.profile_status, company?.usage_start_at]);

	useEffect(() => {
		const paths = location.pathname.split('/');
		setCurrent(paths[2] || '');
	}, [location]);

	useEffect(() => {
		setMenuItems1([
			{
				module: 'main',
				key: '',
				label: t('SIDEBAR.DASHBOARD'),
				icon: (
					<span>
						<Icon name="dashboard" />
					</span>
				),
			},
		]);

		setMenuItems2([
			{
				module: 'profile',
				key: 'profile',
				label: t('SIDEBAR.USER_PROFILE'),
				icon: (
					<span>
						<Icon name="profile" />
					</span>
				),
			},
		]);

		setMenuItems3([
			{
				module: 'wallet',
				key: 'wallet',
				label: t('COMMON.WALLET'),
				icon: (
					<span>
						<Icon name="wallet" />
					</span>
				),
			},
			{
				module: 'plan',
				key: 'plan',
				label: t('COMMON.POINTS'),
				icon: (
					<span>
						<Icon name="plan" />
					</span>
				),
			},
		]);

		// CHECK FOR ACTIVE ACCOUNT
		if (['Active', 'GeneralSuspended'].includes(company?.profile_status!) && company?.usage_start_at) {
			// this check for development purpose only
			setMenuItems1((prev) => [
				...prev,

				authService.isPermittedToActionModule() && {
					module: 'actions',
					key: 'actions',
					label: t('SIDEBAR.ACTIONS'),
					icon: (
						<span>
							<Icon name="actions" />
						</span>
					),
				},

				authService.isPermittedToAction('companyOverview') && {
					module: 'company-information',
					key: 'company-information',
					label: t('SIDEBAR.COMPANY_INFORMATION'),
					icon: (
						<span>
							<Icon name="companies" />
						</span>
					),
				},

				authService.isPermittedToAction('companyOverview') && {
					module: 'shareholders-list',
					key: 'shareholders-list',
					label: t('SIDEBAR.SHAREHOLDERS_LIST'),
					icon: (
						<span>
							<Icon name="list" style={{ transform: isRTL() ? 'rotateY(180deg)' : 'unset' }} />
						</span>
					),
				},

				authService.isPermittedToAction('companyOverview') && {
					module: 'cap-table',
					key: 'cap-table',
					label: t('SIDEBAR.CAP_TABLE'),
					icon: (
						<span>
							<Icon name="pie" />
						</span>
					),
				},

				authService.isPermittedToAction('companyOverview') && {
					module: 'transactions-logs',
					key: 'transactions-logs',
					label: (
						<span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							{t('SIDEBAR.TRANSACTIONS_LOGS')}
							{<Badge count={pendingCount} size="small" />}
						</span>
					),
					icon: (
						<span>
							<Icon name="transaction" />
						</span>
					),
				},
			]);

			if (authService.isPermittedToAction('companyUserManagement'))
				setMenuItems2((prev) => [
					...prev,
					{
						module: 'users-management',
						key: 'users-management',
						label: t('SIDEBAR.USERS_MANAGEMENT'),
						icon: (
							<span>
								<Icon name="users" />
							</span>
						),
					},
				]);
		} else {
			setMenuItems1((prev) => [
				...prev,
				{
					module: 'shareholders-registry',
					key: 'shareholders-registry',
					label:
						company?.company_shareholder_status! == 'Waiting'
							? t('SIDEBAR.ADD_SHAREHOLDERS_REGISTRY')
							: company?.company_shareholder_status! == 'Rejected'
								? t('SIDEBAR.UPDATE_SHAREHOLDERS_REGISTRY')
								: t('SIDEBAR.VIEW_SHAREHOLDERS_REGISTRY'),
					icon: (
						<span>
							<Icon name="users" />
						</span>
					),
				},
				authService.isPermittedToAction('companyDocumentView') && {
					module: 'company-documents',
					key: 'company-documents',
					label:
						company?.profile_status! == 'FieldUpdateRequest'
							? t('SIDEBAR.UPDATE_COMPANY_DOCUMENTS')
							: company?.has_documents
								? t('SIDEBAR.VIEW_COMPANY_DOCUMENTS')
								: t('SIDEBAR.ADD_COMPANY_DOCUMENTS'),
					icon: (
						<span>
							<Icon name="files" />
						</span>
					),
				},
			]);
		}
	}, [t, company, pendingCount]);

	useEffect(() => {
		if (!screens.sm && !collapsed) document.body.style.overflowY = 'hidden';
		if (!screens.sm && collapsed) document.body.style.overflowY = 'auto';
	}, [collapsed]);

	const onMenuClick: MenuProps['onClick'] = (e: any) => {
		if (window.innerWidth <= 576) changeCollapsed(true);

		if (e.key === 'plan') {
			setPlanOpen(true);
			return;
		}

		navigate(e.key);
	};

	return (
		<div>
			<div>
				<h6 className="mt-3 d-sm-none d-lg-block text-light" style={{ paddingInlineStart: 20, minHeight: 20, whiteSpace: 'nowrap' }}>
					{!collapsed && t('SIDEBAR.MAIN_MENU')}
				</h6>
				<Menu items={menuItems1} onClick={onMenuClick} mode="inline" selectedKeys={[current]} />
			</div>
			<hr style={{ marginInline: 20, borderColor: '#aaa' }} />
			<div>
				<h6 className="pt-3 d-sm-none d-lg-block text-light " style={{ paddingInlineStart: 20, minHeight: 36, whiteSpace: 'nowrap' }}>
					{!collapsed && t('SIDEBAR.ACCOUNT_INFORMATION')}
				</h6>
				<Menu items={menuItems2} onClick={onMenuClick} selectedKeys={[current]} />
			</div>

			<div>
				{!screens.sm && (
					<>
						<hr style={{ marginInline: 20, borderColor: '#aaa' }} />
						<Menu items={menuItems3} onClick={onMenuClick} selectedKeys={[current]} />
					</>
				)}
			</div>
		</div>
	);
}

export default MainDashboardMenu;
